import { useEffect, useRef } from "react";

import { useAssetAdoptionsQuery, useArchiveAssetMutation, useUnarchiveAssetMutation, type LibraryAsset } from "@/api/library";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/hooks";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import {
  archiveRefusalFromError,
  archiveRefusalKnownCopy,
} from "@/lib/archiveRefusal";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { LoadingState } from "@/ui/states";

import { AdoptionImpact } from "./AdoptionImpact";

export type LifecycleMode = "archive" | "unarchive";

/**
 * The archive/unarchive confirmation, with the adoption impact shown first.
 *
 * The confirm control stays disabled until the adoption view has actually
 * loaded: confirming a lifecycle change without having seen who pins the asset
 * is the exact prototype defect this ticket corrects, so an unloaded or failed
 * impact read fails closed rather than letting the write through blind.
 *
 * There is deliberately no delete flow here or anywhere else — the contract
 * has no delete route, and archive is the only lifecycle exit. Pins keep
 * resolving across it.
 */
export function LifecycleDialog({
  asset,
  mode,
  onClose,
}: {
  asset: LibraryAsset;
  mode: LifecycleMode;
  onClose: () => void;
}) {
  // `decisionFresh`: this read justifies a lifecycle write, so it refetches on
  // every dialog open instead of serving rows up to `staleTime` old. A pin
  // removed seconds ago must be gone from the impact this decision is made on.
  const adoptions = useAssetAdoptionsQuery(asset.id, { decisionFresh: true });
  const retryAdoptions = useRetryPausedQuery(queryKeys.assetAdoptions(asset.id));
  const archive = useArchiveAssetMutation(asset.id);
  const unarchive = useUnarchiveAssetMutation(asset.id);
  const mutation = mode === "archive" ? archive : unarchive;
  // Only the WRITE locks the dialog. Once the service has accepted it, the
  // outstanding work is the library refresh — reads that can be held for as
  // long as the connection is gone — and a dialog that cannot be left while a
  // read is parked is a dialog with no exit from a write that already
  // succeeded. Close and Escape come back the moment the phase flips, and
  // that is safe to allow because the mutation has ALREADY reconciled the
  // catalog's cache from the server's response before flipping it: there is
  // no pre-write row behind this dialog for an early dismissal to reveal.
  const writing = mutation.phase === "writing";
  const refreshing = mutation.phase === "refreshing";
  const busy = writing;
  // Fail closed on a HELD read as well as an unloaded one: a decision-fresh
  // refetch that failed and parked leaves `isSuccess` true over rows of
  // unknown age, and `isFetching` false. Neither of the other two guards sees
  // it, and it is precisely the stale account #648 forbids deciding on.
  const impactUnknown =
    !adoptions.isSuccess || adoptions.isFetching || adoptions.isPaused;

  // Whether THIS dialog instance is still the one on screen. `mutateAsync`
  // resolves only after the awaited library refresh, and the clinician may
  // have closed this dialog (allowed once the write committed) and opened
  // another — for another asset, possibly mid-write — by then. The mutation
  // keeps running after its observer unmounts, so the continuation below
  // would otherwise call a captured `onClose` that dismisses whichever dialog
  // the parent owns NOW, swallowing that dialog's own refusal (PR #675 review,
  // reproduced). A dismissed instance closes nothing.
  //
  // Reset in the effect BODY, not only initialised: `main.tsx` mounts the app
  // in StrictMode, which in development replays mount effects as
  // effect → cleanup → effect on the same instance, and a cleanup-only latch
  // would leave every dev-session dialog "dismissed" from birth — a committed
  // write would then never close it (round-two review, reproduced). Same
  // replay `Dialog.test.tsx` guards for the focus scope.
  const dismissed = useRef(false);
  useEffect(() => {
    dismissed.current = false;
    return () => {
      dismissed.current = true;
    };
  }, []);

  async function confirm() {
    try {
      await mutation.mutateAsync();
      if (!dismissed.current) {
        onClose();
      }
    } catch {
      // Rendered below from mutation.error; the dialog stays open so the
      // refusal is read in the context of the impact that motivated it.
    }
  }

  // A stale catalog is the case this refusal exists for: the row was archivable
  // when it rendered and the server refused by the time the click landed.
  //
  // Preference order, and the middle rung is the one that matters: our copy for
  // a reason we know, else THE SERVER'S OWN SENTENCE, else generic. Defaulting
  // an unknown reason to generic copy would be a regression — the day
  // `ArchiveRefusalReason` grows a member this build has no copy for, the
  // server is still holding a sentence that describes it exactly, and replacing
  // that with "the server will not change this asset's archive state" throws
  // away the better answer. The disabled tooltip has no such sentence available,
  // which is why it keeps the generic default and this does not.
  const refusalReason = mutation.isError
    ? archiveRefusalFromError(mutation.error)
    : null;
  const serverMessage =
    mutation.error instanceof ApiError ? mutation.error.message : null;
  const failure = mutation.isError
    ? ((refusalReason ? archiveRefusalKnownCopy(refusalReason) : null) ??
      serverMessage ??
      "The Editor Service could not be reached.")
    : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={
          mode === "archive"
            ? `Archive "${asset.name}"?`
            : `Unarchive "${asset.name}"?`
        }
        description={
          mode === "archive"
            ? "Archived assets leave the default catalog and are not offered for new adoption. Projects that already pin a version keep resolving it until they deliberately update."
            : "The asset returns to the active catalog and can be adopted again. Existing pins are unaffected."
        }
        busy={busy}
        footer={
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              {/* Once the write has committed there is nothing left to cancel;
                  the control names what it now does. */}
              {refreshing ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={() => void confirm()}
              disabled={busy || refreshing || impactUnknown}
            >
              {mode === "archive" ? "Archive asset" : "Unarchive asset"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {/* While the decision-fresh read is in flight, rows from a previous
              open are exactly the stale account this dialog must not show
              (PW-LIB-10) — so they are replaced by the loading state, not
              merely disarmed by the disabled confirm below. Not during the
              post-write refresh, though: the decision has been made on these
              rows, and the refresh does not change who pins the asset. */}
          {adoptions.isFetching && !adoptions.isPending && !refreshing ? (
            <LoadingState label="Loading adoption impact" rows={2} />
          ) : (
            <AdoptionImpact adoptions={adoptions} retry={retryAdoptions} />
          )}
          {failure ? (
            <p role="alert" className="text-sm text-destructive">
              {failure}
            </p>
          ) : null}
        </div>
        {/* Mounted for the dialog's whole life and filled only after the write
            commits: assistive technology announces text ADDED to a live region
            it already knows, and can miss a region inserted with its text —
            the same reason `StaleDataNotice` toggles `visible` on a mounted
            node. Normally too brief to read; visible exactly when the refresh
            is being held, which is when the clinician needs to know the write
            is done and that leaving is safe. */}
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-muted-foreground"
        >
          {refreshing
            ? `${mode === "archive" ? "Archived" : "Unarchived"}. The library is refreshing; you can close this dialog.`
            : null}
        </p>
      </DialogContent>
    </Dialog>
  );
}
